(() => {
  'use strict';

  if (!('customElements' in window) || customElements.get('fanttik-checklist')) {
    return;
  }

  const LEGACY_STORAGE_KEY = 'fanttik_checklist';
  const FOCUSABLE_SELECTOR = [
    'a[href]:not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const bodyScrollLockOwners = new Set();
  let bodyOverflowBeforeLock = '';
  let instanceCount = 0;

  function lockBodyScroll(owner) {
    if (!document.body || bodyScrollLockOwners.has(owner)) return;

    if (bodyScrollLockOwners.size === 0) {
      bodyOverflowBeforeLock = document.body.style.overflow;
    }

    bodyScrollLockOwners.add(owner);
    document.body.style.overflow = 'hidden';
  }

  function unlockBodyScroll(owner) {
    if (!bodyScrollLockOwners.delete(owner) || !document.body) return;

    if (bodyScrollLockOwners.size === 0) {
      document.body.style.overflow = bodyOverflowBeforeLock;
      bodyOverflowBeforeLock = '';
    }
  }

  class FanttikChecklist extends HTMLElement {
    constructor() {
      super();

      this._uid = ++instanceCount;
      this._connected = false;
      this._filterState = Object.create(null);
      this._savedState = {};
      this._toastTimers = new Map();
      this._toastDefaultText = new Map();
      this._footerVisible = null;
      this._modalOpen = false;
      this._lastFocusedElement = null;
      this._footerObserver = null;
      this._footerAnimationFrame = null;
      this._modalFocusFrame = null;
      this._modalHideTimer = null;
      this._copyRequestId = 0;

      this._handleClick = this._handleClick.bind(this);
      this._handleChange = this._handleChange.bind(this);
      this._handleDocumentKeydown = this._handleDocumentKeydown.bind(this);
      this._handleShopifyBlockSelect = this._handleShopifyBlockSelect.bind(this);
      this._requestFooterUpdate = this._requestFooterUpdate.bind(this);
    }

    connectedCallback() {
      if (this._connected) return;
      this._connected = true;

      this._cacheElements();
      this._storageKey = this._resolveStorageKey();
      this._initializeFilters();
      this._savedState = this._loadSavedState();
      this._applySavedState();
      this._initializeAccessibility();

      this.addEventListener('click', this._handleClick);
      this.addEventListener('change', this._handleChange);
      document.addEventListener('keydown', this._handleDocumentKeydown);
      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', this._handleShopifyBlockSelect);
      }

      this._updateFilters();
      this._setupFooterVisibility();
    }

    disconnectedCallback() {
      if (!this._connected) return;

      this.removeEventListener('click', this._handleClick);
      this.removeEventListener('change', this._handleChange);
      document.removeEventListener('keydown', this._handleDocumentKeydown);
      document.removeEventListener('shopify:block:select', this._handleShopifyBlockSelect);
      this._teardownFooterVisibility();
      this._closeResetModal({ restoreFocus: false, hideImmediately: true });
      this._clearToasts();

      if (this._modalFocusFrame !== null) {
        cancelAnimationFrame(this._modalFocusFrame);
        this._modalFocusFrame = null;
      }

      if (this._modalHideTimer !== null) {
        clearTimeout(this._modalHideTimer);
        this._modalHideTimer = null;
      }

      this._copyRequestId += 1;
      this._connected = false;
    }

    _cacheElements() {
      this._items = Array.from(this.querySelectorAll('.ft-item-wrapper'));
      this._checkboxes = Array.from(this.querySelectorAll('input.ft-item-checkbox'));
      this._categories = Array.from(this.querySelectorAll('.ft-cat-card'));
      this._progressSticky = this.querySelector('.ft-progress-sticky');
      this._progressTrack = this.querySelector('.ft-progress-track');
      this._progressBar = this.querySelector('.ft-progress-bar, [id^="ProgressBar-"]');
      this._progressPercent = this.querySelector('.ft-progress-percent, [id^="ProgressPercent-"]');
      this._progressCount = this.querySelector('.ft-progress-count, [id^="ProgressCount-"]');
      this._footer = this.querySelector('.ft-footer-bar');
      this._footerPercent = this.querySelector('.ft-footer-percent, [id^="FooterPercent-"]');
      this._footerCount = this.querySelector('.ft-footer-count, [id^="FooterCount-"]');
      this._modalOverlay = this.querySelector('.ft-modal-overlay');
      this._modal = this._modalOverlay && this._modalOverlay.querySelector('.ft-modal');
      this._copyToast = this.querySelector('[id^="FtToast-"]');
      this._resetToast = this.querySelector('[id^="FtResetToast-"]');

      [this._copyToast, this._resetToast].forEach((toast) => {
        if (!toast) return;
        const defaultText = toast.dataset.defaultMessage || toast.textContent.trim();
        this._toastDefaultText.set(toast, defaultText);
        toast.textContent = '';
      });
    }

    _resolveStorageKey() {
      const configuredKey = (this.dataset.storageKey || '').trim();
      if (configuredKey) return configuredKey;

      const identity = this.id || `instance-${this._uid}`;
      return `fanttik_checklist:${window.location.pathname}:${identity}`;
    }

    _initializeFilters() {
      this._filterState = Object.create(null);
      const buttons = Array.from(this.querySelectorAll('.ft-toggle-btn[data-type][data-value]'));
      const types = new Set(buttons.map((button) => button.dataset.type).filter(Boolean));

      types.forEach((type) => {
        const typeButtons = buttons.filter((button) => button.dataset.type === type);
        const activeButton = typeButtons.find((button) => button.classList.contains('active')) || typeButtons[0];

        if (!activeButton) return;
        this._filterState[type] = activeButton.dataset.value;
        typeButtons.forEach((button) => {
          const active = button === activeButton;
          button.classList.toggle('active', active);
          button.setAttribute('aria-pressed', String(active));
        });
      });
    }

    _initializeAccessibility() {
      if (this._progressTrack) {
        this._progressTrack.setAttribute('role', 'progressbar');
        this._progressTrack.setAttribute('aria-valuemin', '0');
        this._progressTrack.setAttribute('aria-valuemax', '100');
        if (
          !this._progressTrack.hasAttribute('aria-label') &&
          !this._progressTrack.hasAttribute('aria-labelledby')
        ) {
          this._progressTrack.setAttribute('aria-label', 'Checklist completion');
        }
      }

      if (this._modalOverlay) {
        this._modalOverlay.setAttribute('aria-hidden', 'true');
        this._modalOverlay.setAttribute('inert', '');
        this._modalOverlay.hidden = true;
      }

      if (this._modal) {
        if (!this._modal.hasAttribute('role')) this._modal.setAttribute('role', 'dialog');
        this._modal.setAttribute('aria-modal', 'true');
        if (!this._modal.hasAttribute('tabindex')) this._modal.setAttribute('tabindex', '-1');

        const title = this._modal.querySelector('.ft-modal-title');
        const description = this._modal.querySelector('.ft-modal-text');

        if (title) {
          if (!title.id) title.id = `FtModalTitle-${this._uid}`;
          this._modal.setAttribute('aria-labelledby', title.id);
        }

        if (description) {
          if (!description.id) description.id = `FtModalDescription-${this._uid}`;
          this._modal.setAttribute('aria-describedby', description.id);
        }
      }

      [this._copyToast, this._resetToast].forEach((toast) => {
        if (!toast) return;
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');
        toast.setAttribute('aria-atomic', 'true');
      });

      this._setFooterVisible(false);
    }

    _loadSavedState() {
      let currentRaw;

      try {
        currentRaw = window.localStorage.getItem(this._storageKey);
      } catch (error) {
        return {};
      }

      if (currentRaw !== null) {
        const currentState = this._parseStoredState(currentRaw);
        if (currentState) return currentState;

        this._persistState({});
        return {};
      }

      const migratedState = {};

      try {
        const legacyRaw = window.localStorage.getItem(LEGACY_STORAGE_KEY);
        const legacyState = legacyRaw === null ? {} : this._parseStoredState(legacyRaw) || {};

        this._checkboxes.forEach((checkbox) => {
          const itemId = checkbox.dataset.itemId;
          if (itemId && Object.prototype.hasOwnProperty.call(legacyState, itemId)) {
            migratedState[itemId] = legacyState[itemId] === true;
          }
        });
      } catch (error) {
        // Storage may be disabled. The component still works for the current page view.
      }

      // An existing, even empty, new-key value is the migration marker.
      this._persistState(migratedState);
      return migratedState;
    }

    _parseStoredState(raw) {
      try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
        return parsed;
      } catch (error) {
        return null;
      }
    }

    _persistState(state) {
      this._savedState = { ...state };

      try {
        window.localStorage.setItem(this._storageKey, JSON.stringify(this._savedState));
      } catch (error) {
        // Keep the in-memory state when storage is unavailable or full.
      }
    }

    _applySavedState() {
      this._checkboxes.forEach((checkbox) => {
        const itemId = checkbox.dataset.itemId;
        checkbox.checked = Boolean(itemId && this._savedState[itemId] === true);
      });
    }

    _handleClick(event) {
      const shareLink = event.target.closest('[data-share-platform]');
      if (shareLink && this.contains(shareLink)) {
        this._openSharePopup(event, shareLink);
        return;
      }

      const actionControl = event.target.closest('[data-ft-action]');
      if (actionControl && this.contains(actionControl)) {
        event.preventDefault();

        switch (actionControl.dataset.ftAction) {
          case 'print':
            window.print();
            break;
          case 'open-reset':
            this._openResetModal(actionControl);
            break;
          case 'close-reset':
            this._closeResetModal();
            break;
          case 'confirm-reset':
            this._resetChecklist();
            break;
          case 'copy':
            this._copyChecklist();
            break;
          case 'filter':
            this._selectFilter(actionControl);
            break;
          default:
            break;
        }
        return;
      }

      const filterButton = event.target.closest('.ft-toggle-btn[data-type][data-value]');
      if (filterButton && this.contains(filterButton)) {
        event.preventDefault();
        this._selectFilter(filterButton);
        return;
      }

      if (event.target === this._modalOverlay) {
        this._closeResetModal();
      }
    }

    _handleChange(event) {
      const checkbox = event.target.closest('input.ft-item-checkbox');
      if (!checkbox || !this.contains(checkbox)) return;

      const itemId = checkbox.dataset.itemId;
      if (itemId) {
        this._savedState[itemId] = checkbox.checked;
        this._persistState(this._savedState);
      }

      this._calculateProgress();
    }

    _handleShopifyBlockSelect(event) {
      const target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      const item = target.closest('.ft-item-wrapper');
      if (!item || !this.contains(item)) return;

      Object.entries({
        duration: item.dataset.duration,
        season: item.dataset.season
      }).forEach(([type, value]) => {
        if (!value || value === 'all') return;

        const matchingButton = Array.from(
          this.querySelectorAll(`.ft-toggle-btn[data-type="${type}"]`)
        ).find((button) => button.dataset.value === value);

        if (matchingButton) this._selectFilter(matchingButton);
      });
    }

    _selectFilter(selectedButton) {
      const type = selectedButton.dataset.type;
      const value = selectedButton.dataset.value;
      const toggle = selectedButton.closest('.ft-toggle');
      const buttons = Array.from(
        (toggle || this).querySelectorAll(`.ft-toggle-btn[data-type="${type}"]`)
      );

      buttons.forEach((button) => {
        const active = button === selectedButton;
        button.classList.toggle('active', active);
        button.setAttribute('aria-pressed', String(active));
      });

      this._filterState[type] = value;
      this._updateFilters();
    }

    _updateFilters() {
      this._items.forEach((item) => {
        const visible = Object.entries(this._filterState).every(([type, selectedValue]) => {
          const itemValue = item.dataset[type];
          return !itemValue || itemValue === 'all' || itemValue === selectedValue;
        });

        item.classList.toggle('hidden', !visible);
        item.setAttribute('aria-hidden', String(!visible));
      });

      this._categories.forEach((category) => {
        const hasVisibleItems = Boolean(category.querySelector('.ft-item-wrapper:not(.hidden)'));
        category.style.display = hasVisibleItems ? '' : 'none';
        category.setAttribute('aria-hidden', String(!hasVisibleItems));
      });

      this._calculateProgress();
    }

    _calculateProgress() {
      const visibleCheckboxes = this._checkboxes.filter((checkbox) => {
        const item = checkbox.closest('.ft-item-wrapper');
        return item && !item.classList.contains('hidden');
      });
      const total = visibleCheckboxes.length;
      const checked = visibleCheckboxes.filter((checkbox) => checkbox.checked).length;
      const remaining = total - checked;
      const percent = total === 0 ? 0 : Math.round((checked / total) * 100);

      if (this._progressBar) this._progressBar.style.width = `${percent}%`;
      if (this._progressPercent) this._progressPercent.textContent = `${percent}% Complete`;
      if (this._progressCount) this._progressCount.textContent = `${remaining} remaining`;
      if (this._footerPercent) this._footerPercent.textContent = `${percent}%`;
      if (this._footerCount) this._footerCount.textContent = `${remaining} remaining`;

      if (this._progressTrack) {
        this._progressTrack.setAttribute('aria-valuenow', String(percent));
        this._progressTrack.setAttribute(
          'aria-valuetext',
          `${percent}% complete, ${remaining} remaining`
        );
      }

      this._categories.forEach((category) => {
        const categoryCheckboxes = Array.from(
          category.querySelectorAll('.ft-item-wrapper:not(.hidden) input.ft-item-checkbox')
        );
        const categoryChecked = categoryCheckboxes.filter((checkbox) => checkbox.checked).length;
        const counter = category.querySelector('.ft-cat-counter, [id^="CatCounter-"]');
        if (counter) counter.textContent = `${categoryChecked}/${categoryCheckboxes.length}`;
      });
    }

    _openSharePopup(event, link) {
      const platform = link.dataset.sharePlatform;
      if (!platform || !link.href || event.defaultPrevented) return;

      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const popup = window.open(
        '',
        '_blank',
        'width=600,height=400,resizable=yes,scrollbars=yes'
      );

      if (!popup) return;

      event.preventDefault();
      popup.opener = null;
      popup.location.replace(link.href);
    }

    _setupFooterVisibility() {
      this._setFooterVisible(false);
      if (!this._footer || !this._progressSticky) return;

      if ('IntersectionObserver' in window) {
        this._footerObserver = new IntersectionObserver(() => {
          this._updateFooterVisibility();
        });
        this._footerObserver.observe(this);
        this._footerObserver.observe(this._progressSticky);
        this._updateFooterVisibility();
        return;
      }

      window.addEventListener('scroll', this._requestFooterUpdate, { passive: true });
      window.addEventListener('resize', this._requestFooterUpdate, { passive: true });
      this._requestFooterUpdate();
    }

    _teardownFooterVisibility() {
      if (this._footerObserver) {
        this._footerObserver.disconnect();
        this._footerObserver = null;
      }

      window.removeEventListener('scroll', this._requestFooterUpdate);
      window.removeEventListener('resize', this._requestFooterUpdate);

      if (this._footerAnimationFrame !== null) {
        cancelAnimationFrame(this._footerAnimationFrame);
        this._footerAnimationFrame = null;
      }

      this._setFooterVisible(false);
    }

    _requestFooterUpdate() {
      if (this._footerAnimationFrame !== null) return;

      this._footerAnimationFrame = requestAnimationFrame(() => {
        this._footerAnimationFrame = null;
        this._updateFooterVisibility();
      });
    }

    _updateFooterVisibility() {
      if (!this._footer || !this._progressSticky || !this.isConnected) {
        this._setFooterVisible(false);
        return;
      }

      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const progressRect = this._progressSticky.getBoundingClientRect();
      const rootRect = this.getBoundingClientRect();
      const progressHasPassed = progressRect.bottom <= 0;
      const rootIsInViewport = rootRect.bottom > 0 && rootRect.top < viewportHeight;

      this._setFooterVisible(progressHasPassed && rootIsInViewport);
    }

    _setFooterVisible(visible) {
      if (!this._footer || this._footerVisible === visible) return;
      this._footerVisible = visible;
      this._footer.classList.toggle('visible', visible);
      this._footer.setAttribute('aria-hidden', String(!visible));
      this._footer.toggleAttribute('inert', !visible);
      if ('inert' in this._footer) this._footer.inert = !visible;
    }

    _openResetModal(trigger) {
      if (!this._modalOverlay || !this._modal || this._modalOpen) return;

      if (this._modalHideTimer !== null) {
        clearTimeout(this._modalHideTimer);
        this._modalHideTimer = null;
      }

      this._modalOpen = true;
      this._lastFocusedElement = trigger || document.activeElement;
      this._modalOverlay.hidden = false;
      // Ensure the hidden state is painted before the opening transition starts.
      void this._modalOverlay.offsetWidth;
      this._modalOverlay.classList.add('show');
      this._modalOverlay.setAttribute('aria-hidden', 'false');
      this._modalOverlay.removeAttribute('inert');
      lockBodyScroll(this);

      this._modalFocusFrame = requestAnimationFrame(() => {
        this._modalFocusFrame = null;
        if (!this._modalOpen) return;

        const focusTarget = this._getFocusableElements()[0] || this._modal;
        focusTarget.focus({ preventScroll: true });
      });
    }

    _closeResetModal(options = {}) {
      const { restoreFocus = true, hideImmediately = false } = options;
      if (!this._modalOpen) {
        if (hideImmediately && this._modalOverlay) this._modalOverlay.hidden = true;
        unlockBodyScroll(this);
        return;
      }

      this._modalOpen = false;

      if (this._modalFocusFrame !== null) {
        cancelAnimationFrame(this._modalFocusFrame);
        this._modalFocusFrame = null;
      }

      const focusTarget = this._lastFocusedElement;
      this._lastFocusedElement = null;
      if (restoreFocus && focusTarget && focusTarget.isConnected && typeof focusTarget.focus === 'function') {
        focusTarget.focus({ preventScroll: true });
      } else if (this._modal && this._modal.contains(document.activeElement)) {
        document.activeElement.blur();
      }

      if (this._modalOverlay) {
        this._modalOverlay.classList.remove('show');
        this._modalOverlay.setAttribute('aria-hidden', 'true');
        this._modalOverlay.setAttribute('inert', '');

        if (this._modalHideTimer !== null) clearTimeout(this._modalHideTimer);

        if (hideImmediately) {
          this._modalOverlay.hidden = true;
          this._modalHideTimer = null;
        } else {
          this._modalHideTimer = window.setTimeout(() => {
            this._modalHideTimer = null;
            if (!this._modalOpen && this._modalOverlay) this._modalOverlay.hidden = true;
          }, 350);
        }
      }

      unlockBodyScroll(this);
    }

    _handleDocumentKeydown(event) {
      if (!this._modalOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this._closeResetModal();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = this._getFocusableElements();
      if (focusableElements.length === 0) {
        event.preventDefault();
        this._modal.focus({ preventScroll: true });
        return;
      }

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (!this._modal.contains(activeElement)) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      } else if (event.shiftKey && activeElement === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    _getFocusableElements() {
      if (!this._modal) return [];

      return Array.from(this._modal.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
        return element.getClientRects().length > 0 && element.getAttribute('aria-hidden') !== 'true';
      });
    }

    _resetChecklist() {
      this._checkboxes.forEach((checkbox) => {
        checkbox.checked = false;
      });

      // Keep an empty value at the new key so legacy data is never migrated again.
      this._persistState({});
      this._calculateProgress();
      this._closeResetModal();
      this._showToast(this._resetToast);
    }

    _buildCopyText() {
      let text = '\u{1F697} Fanttik Checklist\n\n';

      this._categories.forEach((category) => {
        if (category.style.display === 'none' || category.hidden) return;

        const heading = category.querySelector(
          '.ft-cat-header h1, .ft-cat-header h2, .ft-cat-header h3, .ft-cat-header h4, .ft-cat-title'
        );
        if (heading) text += `[ ${heading.textContent.trim()} ]\n`;

        category.querySelectorAll('.ft-item-wrapper:not(.hidden)').forEach((item) => {
          const checkbox = item.querySelector('input.ft-item-checkbox');
          const itemText = item.querySelector('.ft-item-text');
          if (!itemText) return;

          text += `${checkbox && checkbox.checked ? ' [\u2713] ' : ' [ ] '}${itemText.textContent.trim()}\n`;
        });

        text += '\n';
      });

      return text;
    }

    async _copyChecklist() {
      const requestId = ++this._copyRequestId;
      const text = this._buildCopyText();
      let copied = false;

      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        try {
          await navigator.clipboard.writeText(text);
          copied = true;
        } catch (error) {
          copied = false;
        }
      }

      if (!copied) copied = this._copyWithExecCommand(text);
      if (!this._connected || requestId !== this._copyRequestId) return;

      if (copied) {
        this._showToast(this._copyToast);
      } else {
        this._showToast(this._copyToast, 'Unable to copy. Please try again.');
      }
    }

    _copyWithExecCommand(text) {
      const textarea = document.createElement('textarea');
      const previousFocus = document.activeElement;

      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.setAttribute('aria-hidden', 'true');
      textarea.style.position = 'fixed';
      textarea.style.top = '0';
      textarea.style.left = '-9999px';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus({ preventScroll: true });
      textarea.select();
      textarea.setSelectionRange(0, textarea.value.length);

      let copied = false;
      try {
        copied = document.execCommand('copy');
      } catch (error) {
        copied = false;
      } finally {
        textarea.remove();
        if (previousFocus && previousFocus.isConnected && typeof previousFocus.focus === 'function') {
          previousFocus.focus({ preventScroll: true });
        }
      }

      return copied;
    }

    _showToast(toast, message) {
      if (!toast || !this._connected) return;

      const existingTimer = this._toastTimers.get(toast);
      if (existingTimer) clearTimeout(existingTimer);

      const defaultText = this._toastDefaultText.get(toast) || toast.dataset.defaultMessage || '';
      toast.textContent = message || defaultText;
      toast.classList.add('show');

      const timer = window.setTimeout(() => {
        if (this._toastTimers.get(toast) !== timer) return;
        toast.classList.remove('show');
        toast.textContent = '';
        this._toastTimers.delete(toast);
      }, 3000);

      this._toastTimers.set(toast, timer);
    }

    _clearToasts() {
      this._toastTimers.forEach((timer, toast) => {
        clearTimeout(timer);
        toast.classList.remove('show');
        toast.textContent = '';
      });
      this._toastTimers.clear();
      this._toastDefaultText.clear();
    }
  }

  customElements.define('fanttik-checklist', FanttikChecklist);
})();
